import { Select } from 'antd';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MultiSelectEntity from '../RefineSearch/MultiSelectEntity';
import Popup from '../Common/Popup';

function InvestorHeadquartersFilter({ HeadquartersOptions, HeadquarterCityOptions, setHeadquarterCityOptions, setHeadquartersOptions, customPathname, label = "Investor Headquarters", isPopupOpen, setIsPopupOpen }) {
    const { InvestorHeadquarters, refineSearchFilters } = useContext(PeFilterContext);
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;
    
    // Store original headquarters options to restore when switching between India/Non-India
    const originalHeadquartersRef = useRef(null);
    
    const [InvestorCountry, setInvestorCountry] = useState([
        {
            id: 1,
            name: "India",
            code: "IN",
            isChecked: false
        },
        {
            id: 2,
            name: "Non-India",
            code: "NON_IN",
            isChecked: false
        }
    ]);

    useEffect(() => {
        const industryIds = refineSearchFilters[customPathname]?.investorHeadquarters?.map(value => value.id) ?? [];
        const updatedOptions = InvestorHeadquarters?.map(industry => ({ ...industry, isChecked: industryIds.includes(industry.id) }));
        
        // Store original options for restoration
        if (updatedOptions && !originalHeadquartersRef.current) {
            originalHeadquartersRef.current = updatedOptions;
        }
        
        setHeadquartersOptions(updatedOptions);
    }, [InvestorHeadquarters, customPathname, refineSearchFilters, setHeadquartersOptions]);

    const handleInvestorCountryChange = (selectedValue) => {
        const isIndiaSelected = selectedValue === 1;
        setInvestorCountry(InvestorCountry.map(type => ({ ...type, isChecked: type.id === selectedValue })));
        setHeadquarterCityOptions(HeadquarterCityOptions.map(type => ({ ...type, isChecked: false })));

        if (isIndiaSelected) {
            const selectedCityLength = HeadquarterCityOptions.filter(option => option.isChecked).length;
            setHeadquartersOptions([{ id: "IN", name: "India", isChecked: selectedCityLength === 0 }]);
        } else {
            // When Non-India is selected, restore original options and add hidden "NON_IN" option
            const originalOptions = originalHeadquartersRef.current || HeadquartersOptions;
            const nonIndiaOption = { id: "NON_IN", name: "Non-India", isChecked: true, hidden: true };
            const otherCountries = originalOptions.map(type => ({ ...type, isChecked: false }));
            setHeadquartersOptions([nonIndiaOption, ...otherCountries]);
        }
    };

    const handleCityChange = (e) => {
        const { name, checked, id, selectedEntities, filteredIds } = e.target;
        let updatedOptions;
    
        if (name === "allSelect") {
            updatedOptions = HeadquarterCityOptions.map(city => ({ ...city, isChecked: checked }));
        } else if (name === "filteredSelect" && filteredIds) {
            updatedOptions = HeadquarterCityOptions.map(city => ({
                ...city,
                isChecked: filteredIds.includes(parseInt(city.id)) ? checked : city.isChecked
            }));
        } else if (name === "selectedEntities" && selectedEntities) {
            const selectedEntityIds = selectedEntities.map(entity => parseInt(entity.id));
            updatedOptions = HeadquarterCityOptions.map(city => ({
                ...city,
                isChecked: selectedEntityIds.includes(parseInt(city.id)) ? !city.isChecked : city.isChecked
            }));
        } else {
            updatedOptions = HeadquarterCityOptions.map(city => 
                city.id === parseInt(id) ? { ...city, isChecked: checked } : city
            );
        }
    
        setHeadquarterCityOptions(updatedOptions);
    };
    
    const handleCountryChange = (e) => {
        const { name, checked, id, selectedEntities, filteredIds } = e.target;
        let updatedOptions;
    
        if (name === "allSelect") {
            updatedOptions = HeadquartersOptions.map(country => ({ ...country, isChecked: checked }));
        } else if (name === "filteredSelect" && filteredIds) {
            updatedOptions = HeadquartersOptions.map(country => ({
                ...country,
                isChecked: filteredIds.includes(country.id) ? checked : country.isChecked
            }));
        } else if (name === "selectedEntities" && selectedEntities) {
            const selectedEntityIds = selectedEntities.map(entity => entity.id);
            updatedOptions = HeadquartersOptions.map(country => ({
                ...country,
                isChecked: selectedEntityIds.includes(country.id) ? !country.isChecked : country.isChecked
            }));
        } else {
            updatedOptions = HeadquartersOptions.map(country =>
                country.id === id ? { ...country, isChecked: checked } : country
            );
        }

        // Remove hidden "Non-India" option if it exists
        updatedOptions = updatedOptions.filter(option => option.id !== "NON_IN");

        // Check if any country is selected (excluding hidden options)
        const hasSelectedCountries = updatedOptions.some(option => option.isChecked && !option.hidden);

        // If no countries are selected, add back the hidden "Non-India" option
        if (!hasSelectedCountries) {
            const nonIndiaOption = { id: "NON_IN", name: "Non-India", isChecked: true, hidden: true };
            updatedOptions = [nonIndiaOption, ...updatedOptions];
        }

        setHeadquartersOptions(updatedOptions);
    };

    const countryOption = InvestorCountry.map(data => ({ value: data.id, label: data.name }));

    const selectedCountry = InvestorCountry.find(option => option.isChecked);

    const selectedCityLength = HeadquarterCityOptions?.filter(option => option.isChecked).length;
    const selectedCountryLength = HeadquartersOptions?.filter(option => option.isChecked).length;
    const selectedItemLength = selectedCityLength > 0 ? selectedCityLength : selectedCountryLength;

    const getCountBadge = () => {
        return selectedItemLength > 0 ? selectedItemLength : null;
    };

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100  text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. India, USA</span></span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {getCountBadge() && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {getCountBadge()}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)} label={label}>
                    <div className="p-4">
                        <div className='bg-white rounded'>
                            {InvestorCountry.map((country) => (
                                <label key={country.id} className="flex items-center space-x-2 hover:bg-gray-50 rounded cursor-pointer">
                                    <input
                                        type="radio"
                                        name="investorCountry"
                                        value={country.id}
                                        checked={country.isChecked}
                                        onChange={() => handleInvestorCountryChange(country.id)}
                                        className="text-[#BC8B20] focus:ring-[#BC8B20]"
                                    />
                                    <span className="text-sm font-medium p-2">{country.name}</span>
                                </label>
                            ))}
                        </div>
                        {selectedCountry && (
                            <div>
                                {selectedCountry.name === 'India' ? (
                                    <MultiSelectEntity
                                        handleEntityChange={handleCityChange}
                                        primaryEntity={HeadquarterCityOptions}
                                        city="city"
                                    />
                                ) : (
                                    <MultiSelectEntity
                                        handleEntityChange={handleCountryChange}
                                        primaryEntity={HeadquartersOptions.filter(option => !option.hidden)}
                                        city="country"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default InvestorHeadquartersFilter;